import { Box } from "@mui/material";
import type { DateTime } from "luxon";
import { type RefObject, type SyntheticEvent, useMemo, useState } from "react";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { useRoleColors } from "../../../hooks/useRoleColors";
import { useRosterLookup } from "../../../hooks/useRosterLookup";
import type { DepartmentLocation, NurseFullRole } from "../../../utils/models";
import { facilityToString, getDepartmentNurseTypes, getUniqueLocations } from "../../../utils/utilFunctions";
import { useCheckOverlappingShiftsAndPatch } from "../../staffing/hooks/useHandleCheckOverlappingShifts";
import type { SystemAutoCompleteOption } from "../../UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import type { DropdownItem } from "../../UIToolkit/Inputs/SystemDropDown";

type Props = {
	facility?: DepartmentLocation;
	clickedDate: DateTime;
	onClose?: ((didUpdateOrCreate?: boolean) => void) | ((didUpdateOrCreate?: boolean) => Promise<void>);
	/**
	 * Ref from parent to control did change in inner state.
	 */
	didChangeRef?: RefObject<boolean>;
	/**
	 * Triggered when the didChangeRef is true, and we attempt to close
	 */
	onWarnOnClose?: () => void;
};

export const useMedicalDepartmentPostShiftState = ({ didChangeRef, onWarnOnClose, onClose, clickedDate, facility }: Props) => {
	const roleColor = useRoleColors();
	const { chosenDepartment } = useChosenDepartment();
	const { roster } = useRosterLookup(chosenDepartment.department_id);
	// DEFAULT: the department's own roles until the roster loads; the roster only adds roles staff hold beyond them.
	const departmentNurseTypes = useMemo(() => getDepartmentNurseTypes(chosenDepartment.roles, roster ?? []), [chosenDepartment.roles, roster]);
	const { trackError } = useAnalytics();
	const { checkForOverlapsAndMergeIfUserApproved, patchLoading, postLoading } = useCheckOverlappingShiftsAndPatch();
	const isLoading = patchLoading || postLoading;
	const facilitiesList = getUniqueLocations(chosenDepartment.locations, false, true)
		.filter((location) => !!location.location)
		.sort((locA, locB) => locA.location.localeCompare(locB.location))
		.map<SystemAutoCompleteOption<DepartmentLocation>>((location) => ({
			value: location,
			label: facilityToString(location),
		}));
	const rolesOptions = useMemo(() => {
		return departmentNurseTypes.map<DropdownItem<NurseFullRole>>((role) => ({
			value: role,
			label: role.match(/\((.*?)\)/)?.[1] || role,
			icon: (
				<Box
					sx={{
						bgcolor: roleColor(role).dot,
						width: 8,
						height: 8,
						borderRadius: "50%",
						ml: 0.5,
						mr: 0.5,
					}}
				/>
			),
		}));
	}, [departmentNurseTypes, roleColor]);

	const [state, setState] = useState<{
		selectedFacility: SystemAutoCompleteOption<DepartmentLocation> | null;
		roles: NurseFullRole[];
		currentDate: DateTime;
		keepOpenAfterSave: boolean;
	}>({
		selectedFacility: facilitiesList.find((curr) => curr.value.id === facility?.id) || null,
		roles: departmentNurseTypes,
		currentDate: clickedDate,
		keepOpenAfterSave: false,
	});
	const handleSetState = (newState: Partial<typeof state>) => {
		setState((prevState) => ({ ...prevState, ...newState }));
		if (didChangeRef) {
			didChangeRef.current = true;
		}
	};
	const onSelectedFacilityChange = (_: SyntheticEvent, facility: SystemAutoCompleteOption<DepartmentLocation> | null) => {
		if (facility) {
			handleSetState({
				selectedFacility: facility,
			});
		}
	};
	const handleRoleChange = (value: NurseFullRole[]) => {
		handleSetState({
			roles: value,
		});
	};

	const handleDateChange = (newValue: DateTime) => {
		handleSetState({
			currentDate: newValue,
		});
	};

	const handleCloseClick = () => {
		if (didChangeRef?.current && onWarnOnClose) {
			onWarnOnClose();
		} else {
			onClose?.(false);
		}
	};

	const handleKeepOpenChange = (_: SyntheticEvent, checked: boolean) => {
		handleSetState({
			keepOpenAfterSave: checked,
		});
	};

	const actionAfterSuccess = () => {
		if (!state.keepOpenAfterSave) {
			onClose?.(true);
		} else if (didChangeRef) {
			didChangeRef.current = false;
		}
	};

	const handleSaveClick = async () => {
		if (!state.roles || !state.selectedFacility) {
			trackError("medical_department_post_shift_save_failed", {
				reason: "Missing roles or facility",
				roles: JSON.stringify(state.roles),
				facility: JSON.stringify(state.selectedFacility),
			});
			return;
		}
		const datetimeForPost = state.currentDate.set({ hour: 13, minute: 0 });
		await checkForOverlapsAndMergeIfUserApproved({
			panel: "quick-post",
			shiftStart: datetimeForPost,
			shiftEnd: datetimeForPost,
			selectedRoles: state.roles,
			neededNurses: 1,
			bonus: 0,
			chargeOnly: false,
			location: state.selectedFacility.value.location,
			actionsWhenMergeNotNeeded: {
				onSuccess: actionAfterSuccess,
			},
			actionAfterMerge: actionAfterSuccess,
		});
	};

	const saveDisabled = !state.selectedFacility || state.roles.length === 0;

	return {
		state,
		facilitiesList,
		onSelectedFacilityChange,
		rolesOptions,
		handleRoleChange,
		handleDateChange,
		handleCloseClick,
		handleKeepOpenChange,
		handleSaveClick,
		saveDisabled,
		isLoading,
	};
};
